/**
 * PostgREST paging + `.in()` chunking for the FRONTEND data layer.
 *
 * Mirrors services/api/src/lib/fetchAllRows.ts (the backend precedent, PERF-4).
 * Two distinct silent-corruption hazards are covered here:
 *
 *  1. ROW CAP — Supabase/PostgREST returns at most `db.max-rows` (default 1000)
 *     rows per request. A `.select()` that matches more than that is SILENTLY
 *     truncated: the caller gets 1000 rows and `error: null`, so any aggregate
 *     computed over the result (progress %, term average) is WRONG, not slow.
 *     `fetchAllRows` ranges through the full result set instead.
 *
 *  2. URL LENGTH — `.in('id', ids)` is serialised into the query string. A few
 *     thousand uuids blows past proxy/CDN URL limits and the request fails (or,
 *     worse, is silently rejected by an intermediary). `fetchAllRowsIn` splits
 *     the id list into chunks and concatenates the results.
 *
 * Usage — pass a factory that applies `.range(from, to)` to the query:
 *
 *   const { data, error } = await fetchAllRows((from, to) =>
 *     supabase.from('activities').select('id, course_id').range(from, to),
 *   );
 *
 *   const { data, error } = await fetchAllRowsIn(studentIds, (ids, from, to) =>
 *     supabase.from('student_grades').select('...').in('student_id', ids).range(from, to),
 *   );
 *
 * Errors short-circuit (returns whatever was gathered plus the error) so callers
 * keep their existing `if (error) throw error` handling.
 */

/** PostgREST's default `db.max-rows`. A full page means "there may be more". */
export const PAGE_SIZE = 1000;

/** Max uuids per `.in()` filter — keeps the request URL well inside proxy limits. */
export const IN_CHUNK_SIZE = 200;

// Safety backstop: refuse to loop forever if a backend ever ignores `.range()`.
// 1000 pages x 1000 rows = 1,000,000 rows, far above any real cohort.
const MAX_PAGES = 1000;

export interface PageResult<T> {
  data: T[] | null;
  error: unknown;
}

/**
 * Read every row of a query, one `.range()` page at a time.
 *
 * Termination is on a SHORT page (fewer rows than requested). A page that comes
 * back exactly full is ambiguous — it may be the last page or it may be the row
 * cap biting — so we always ask for one more. That is the whole point: never
 * stop on a full page, or the truncation returns silently.
 */
export async function fetchAllRows<T>(
  makeQuery: (from: number, to: number) => PromiseLike<PageResult<T>>,
  pageSize: number = PAGE_SIZE,
): Promise<{ data: T[]; error: unknown }> {
  const out: T[] = [];
  let from = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const { data, error } = await makeQuery(from, from + pageSize - 1);
    if (error) return { data: out, error };
    const rows = data ?? [];
    out.push(...rows);
    if (rows.length < pageSize) return { data: out, error: null };
    from += pageSize;
  }
  // Hit the backstop — return what we have without an error; this only happens
  // on pathological row counts (>1M) or a backend that ignores range.
  return { data: out, error: null };
}

/** Split an id list into `.in()`-sized chunks. Returns [] for an empty list. */
export function chunkIds(ids: readonly string[], size: number = IN_CHUNK_SIZE): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size) as string[]);
  }
  return chunks;
}

/**
 * Read every row of an `.in(column, ids)` query: chunk the id list, then page
 * each chunk to the end. Chunks run sequentially so a large cohort cannot fan
 * out into an unbounded burst of parallel requests.
 */
export async function fetchAllRowsIn<T>(
  ids: readonly string[],
  makeQuery: (idChunk: string[], from: number, to: number) => PromiseLike<PageResult<T>>,
  opts: { pageSize?: number; chunkSize?: number } = {},
): Promise<{ data: T[]; error: unknown }> {
  const out: T[] = [];
  if (ids.length === 0) return { data: out, error: null };

  for (const chunk of chunkIds(ids, opts.chunkSize ?? IN_CHUNK_SIZE)) {
    const { data, error } = await fetchAllRows<T>(
      (from, to) => makeQuery(chunk, from, to),
      opts.pageSize ?? PAGE_SIZE,
    );
    out.push(...data);
    if (error) return { data: out, error };
  }
  return { data: out, error: null };
}
